import { Box, Heading as ChakraHeading, Link, Text } from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';

const SectionHeading = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <ChakraHeading
    as="h2"
    fontSize={{ base: '2xl', md: '3xl' }}
    style={{ fontFamily: 'Bebas Neue' }}
    mt={10}
    mb={4}
  >
    {children}
  </ChakraHeading>
);

const Paragraph = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <Text mb={4}>{children}</Text>
);

const ContactLink = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <Link
    asChild
    color="blue.600"
    _dark={{ color: 'blue.300' }}
    textDecoration="underline"
  >
    <RouterLink to="/contact-us">{children}</RouterLink>
  </Link>
);

const DataDeletion = (): React.JSX.Element => {
  return (
    <LegalPageLayout
      title="How to delete your data from Olympiq"
      lastUpdated="29 April 2026"
    >
      <Paragraph>
        If you would like Olympiq to delete data we hold about you, follow the
        instructions below for your situation.
      </Paragraph>

      <SectionHeading>If you are a club member</SectionHeading>
      <Paragraph>
        Contact your club&apos;s membership secretary in the first instance. Your
        club is the data controller for your member data, and we will assist them
        in fulfilling your request.
      </Paragraph>
      <Paragraph>
        If you would prefer to contact us directly, send us a message via our{' '}
        <ContactLink>contact page</ContactLink> and start your message with{' '}
        <em>&ldquo;Data deletion request&rdquo;</em>. We will acknowledge within
        7 days and complete deletion within 30 days.
      </Paragraph>

      <SectionHeading>
        If your club uses Olympiq and you want to disconnect Instagram
      </SectionHeading>
      <Paragraph>
        A club administrator can disconnect Instagram at any time from the club
        admin dashboard. On disconnection, the access token and all synced media
        will be deleted within 30 days.
      </Paragraph>
      <Paragraph>
        You may also revoke Olympiq from your Instagram account directly:
      </Paragraph>
      <Box as="ol" className="list-decimal pl-6" mb={4}>
        <li>Open Instagram</li>
        <li>
          Go to <strong>Settings → Apps and Websites → Active</strong>
        </li>
        <li>
          Find <strong>Olympiq</strong> and tap <strong>Remove</strong>
        </li>
      </Box>
      <Paragraph>
        Meta will notify us automatically and we will delete the associated data
        within 30 days.
      </Paragraph>

      <SectionHeading>
        If you are a club and want to close your Olympiq account
      </SectionHeading>
      <Paragraph>
        Send us a message via our <ContactLink>contact page</ContactLink> from
        your registered admin email address and start your message with{' '}
        <em>&ldquo;Close club account&rdquo;</em>. We will confirm within 7 days
        and complete deletion within 30 days.
      </Paragraph>

      <SectionHeading>Questions</SectionHeading>
      <Paragraph>
        If you have questions about deletion, please{' '}
        <ContactLink>contact us</ContactLink>.
      </Paragraph>
    </LegalPageLayout>
  );
};

export default DataDeletion;
